import {createFeatureSelector, createSelector} from "@ngrx/store";
import {fvSensitivityStoreFeatureKey} from "./fv-sensitivity.reducer";
import {ALL_CASE_TOKENS, FvSensitivityState} from "./fv-sensitivity.state";
import {isFundPageLoading} from "../../store/fund/fund.selectors";
import {ScenarioType} from "../../../shared/model/scenario-type";
import {CashflowWeightedCase} from "../../models/cashflow-weighted-case";
import {TraceableMultiple, TraceablePercent} from "../../../shared/model/traceable";
import {FundCashFlowsUtil} from "../../utils/fund-cash-flows-util";

// Maps each perf token to the actual metric column suffixes it covers
const PERF_TOKEN_MAP: Record<string, string[]> = {
    netPerf: ["netPerformanceTvpi", "netPerformanceIrr"],
    grossPerf: ["grossPerformanceTvpi", "grossPerformanceIrr"],
    nav: ["navUplift"],
    finalPrimary: ["finalFundNTvpi"],
};

// Column def names for the case-level group header row (groupsScnHeader)
const CASE_SCN_HEADER: Record<string, string> = {
    weighted: "groupsWeighted",
    low: "groupsLow",
    base: "groupsBase",
    high: "groupsHigh",
};

// Column def name for the perf-group header within a case (groupsHeader)
function perfGroupHeaderKey(caseKey: string, perfToken: string): string {
    const casePart = caseKey === "weighted" ? "" : caseKey.charAt(0).toUpperCase() + caseKey.slice(1);
    const perfMap: Record<string, string> = {
        netPerf: "NetPerformance",
        grossPerf: "GrossPerformance",
        nav: "Nav",
        finalPrimary: "FinalFund",
    };
    return `groups${casePart}${perfMap[perfToken]}`;
}

// Derives the actual table column ID from a case key + metric suffix
function colId(caseKey: string, metric: string): string {
    if (caseKey === "weighted") return metric;
    return caseKey + metric.charAt(0).toUpperCase() + metric.slice(1);
}

export const fvSensitivityState = createFeatureSelector<FvSensitivityState>(fvSensitivityStoreFeatureKey);

export const selectFundValuationSensitivityOverview = createSelector(
    fvSensitivityState,
    (state) => state.fundValuationSensitivityOverview
);

export const selectFundValuationSensitivities = createSelector(
    selectFundValuationSensitivityOverview,
    (overview) => overview?.data.fundValuationSensitivities
);

export const selectFundValuationSensitivityLoading = createSelector(
    selectFundValuationSensitivityOverview,
    isFundPageLoading,
    (overview, isFundLoading) =>
        overview.status === "LOADING" || isFundLoading
);

export const selectFundValuationSensitivityOverviewError = createSelector(
    selectFundValuationSensitivityOverview,
    (overview) => overview.status === "ERROR"
);

export const selectHasSensitivityDetails = createSelector(
    fvSensitivityState,
    (state) => !!state.fundValuationSensitivity.data.id
);

export const selectSensitivityDetails = createSelector(
    fvSensitivityState,
    (state) => state.fundValuationSensitivity
);

export const selectSensitivityDetailsLoading = createSelector(
    selectSensitivityDetails,
    (apiData) => apiData.status === "LOADING"
);

export const selectSensitivityDetailsCashflowForScnType = (scenarioType: ScenarioType) => createSelector(
    selectSensitivityDetails,
    (details) => {
        if (scenarioType === "LOW") {
            return details.data.aggregatedFundCashflowsLow;
        } else if (scenarioType === "BASE") {
            return details.data.aggregatedFundCashflowsBase;
        } else if (scenarioType === "HIGH") {
            return details.data.aggregatedFundCashflowsHigh;
        } else if (scenarioType === "WEIGHTED") {
            return details.data.aggregatedFundCashflowsWeighted;
        } else {
            return undefined;
        }
    }
);

// BELONGS TO selectSensitivityDetailsReturnSummaryTable
export interface ReturnSummaryRow {
    scenarioKey: ScenarioKey;
    scenarioLabel: string;
    probability: TraceablePercent;
    netPerformanceTvpi: TraceableMultiple;
    netPerformanceIrr: TraceablePercent;
    netPerformancePreCarryTvpi: TraceableMultiple;
    netPerformancePreCarryIrr: TraceablePercent;
    investmentPerformanceTvpi: TraceableMultiple;
    investmentPerformanceIrr: TraceablePercent;
    grossPerformanceTvpi: TraceableMultiple;
    grossPerformanceIrr: TraceablePercent;
    navUplift: TraceablePercent;
    finalFundNTvpi: TraceableMultiple;
    isWeighted?: boolean;
}

type ScenarioKey = "low" | "base" | "high" | "weighted";


const order: Array<{ key: ScenarioKey; label: string }> = [
    {key: "low", label: "Low"},
    {key: "base", label: "Base"},
    {key: "high", label: "High"},
    {key: "weighted", label: "CF weighted case"},
];

export const selectSensitivityDetailsReturnSummaryTable = createSelector(
    selectSensitivityDetails,
    (details) => {
        const summary = details.data.sensitivityReturnSummary

        const rows: CashflowWeightedCase[] = [];

        for (const {key, label} of order) {
            const c = summary?.[key];
            if (!c) continue;
            rows.push({
                scenarioKey: key,
                scenarioLabel: label,
                probability: c.probability,
                netPerformanceTvpi: c.netPerformanceTvpi,
                netPerformanceIrr: c.netPerformanceIrr,
                netPerformancePreCarryTvpi: c.netPerformancePreCarryTvpi,
                netPerformancePreCarryIrr: c.netPerformancePreCarryIrr,
                investmentPerformanceTvpi: c.investmentPerformanceTvpi,
                investmentPerformanceIrr: c.investmentPerformanceIrr,
                grossPerformanceTvpi: c.grossPerformanceTvpi,
                grossPerformanceIrr: c.grossPerformanceIrr,
                navUplift: c.navUplift,
                finalFundNTvpi: c.finalFundNTvpi,
                isWeighted: key === "weighted"
            } as ReturnSummaryRow);
        }

        const displayedColumns = [
            "scenarioLabel",
            "probability",
            "netPerformanceTvpi",
            "netPerformanceIrr",
            "grossPerformanceTvpi",
            "grossPerformanceIrr",
            "navUplift",
            "finalFundNTvpi"
        ];


        const headerGroups = [
            {label: "", colspan: 1},                 // Scenario
            {label: "", colspan: 1},
            {label: "Net Performance", colspan: 2},  // TVPI, IRR
            {label: "Gross Performance", colspan: 2},// TVPI, IRR
            {label: "NAV", colspan: 1},              // Uplift
            {label: "Final Primary", colspan: 1},    // nTVPI
        ];

        return {rows, displayedColumns, headerGroups};
    }
);

export const selectColumnTokens = createSelector(
    fvSensitivityState,
    (state) => state.selectedColumnTokens
);

export interface SensitivityColspanMap {
    [colId: string]: number;
}

export const selectSensitivityDisplayedColumns = createSelector(
    selectColumnTokens,
    (tokens) => {
        const cols = ["name"];
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            for (const metric of PERF_TOKEN_MAP[perfToken] ?? []) {
                cols.push(colId(caseKey, metric));
            }
        }
        return cols;
    }
);

export const selectSensitivityGroupsHeader = createSelector(
    selectColumnTokens,
    (tokens) => {
        const header = ["groupsName"];
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            header.push(perfGroupHeaderKey(caseKey, perfToken));
        }
        return header;
    }
);

export const selectSensitivityGroupsScnHeader = createSelector(
    selectColumnTokens,
    (tokens) => {
        const header = ["groupsScnName"];
        for (const caseKey of ALL_CASE_TOKENS) {
            if (tokens.some(t => t.startsWith(caseKey + "_"))) {
                header.push(CASE_SCN_HEADER[caseKey]);
            }
        }
        return header;
    }
);

export const selectSensitivityColspanMap = createSelector(
    selectColumnTokens,
    (tokens) => {
        const map: SensitivityColspanMap = {};
        for (const token of tokens) {
            const sep = token.indexOf("_");
            const caseKey = token.substring(0, sep);
            const perfToken = token.substring(sep + 1);
            const metricCount = (PERF_TOKEN_MAP[perfToken] ?? []).length;
            const perfHeaderKey = perfGroupHeaderKey(caseKey, perfToken);
            map[perfHeaderKey] = metricCount;
            const caseHeaderKey = CASE_SCN_HEADER[caseKey];
            map[caseHeaderKey] = (map[caseHeaderKey] ?? 0) + metricCount;
        }
        return map;
    }
);

// Identifies the perf-group header and first metric column that start each case
// section, so the case-separator left-border follows the currently-first column
// rather than being pinned to Net Performance.
export const selectSensitivityLeftBorderKeys = createSelector(
    selectColumnTokens,
    (tokens) => {
        const headerKeys = new Set<string>();
        const colKeys = new Set<string>();
        for (const caseKey of ALL_CASE_TOKENS) {
            const firstToken = tokens.find(t => t.startsWith(caseKey + "_"));
            if (!firstToken) continue;
            const perfToken = firstToken.substring(firstToken.indexOf("_") + 1);
            headerKeys.add(perfGroupHeaderKey(caseKey, perfToken));
            const firstMetric = (PERF_TOKEN_MAP[perfToken] ?? [])[0];
            if (firstMetric) colKeys.add(colId(caseKey, firstMetric));
        }
        return {headerKeys, colKeys};
    }
);

// Scenario cashflow graph data sourced from the sensitivity-details slice — used
// by FundCashflowsGraphComponent when [isSensitivityView]="true". Colours mirror
// the live-valuation scenario graphs in fund.selectors so the two views look
// identical except for the underlying data.
export const selectFvSensitivityDetailsLowScenarioCashflowsGraph = createSelector(
    selectSensitivityDetailsCashflowForScnType("LOW"),
    (cashflows) => FundCashFlowsUtil.createChartData(cashflows, "#D47474")
);

export const selectFvSensitivityDetailsBaseScenarioCashflowsGraph = createSelector(
    selectSensitivityDetailsCashflowForScnType("BASE"),
    (cashflows) => FundCashFlowsUtil.createChartData(cashflows, "#E8A15B")
);

export const selectFvSensitivityDetailsHighScenarioCashflowsGraph = createSelector(
    selectSensitivityDetailsCashflowForScnType("HIGH"),
    (cashflows) => FundCashFlowsUtil.createChartData(cashflows, "#6FBF73")
);

export const selectFvSensitivityDetailsWeightedScenarioCashflowsGraph = createSelector(
    selectSensitivityDetailsCashflowForScnType("WEIGHTED"),
    (cashflows) => FundCashFlowsUtil.createChartData(cashflows, "grey")
);
